import React, { useState, useEffect, useCallback } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Textarea } from './ui/textarea';
import { 
  Mail, Phone, MessageSquare, User, Calendar, Clock,
  Plus, Search, Filter, ChevronDown, ChevronRight,
  Send, PhoneCall, PhoneIncoming, PhoneOutgoing, PhoneMissed,
  FileText, Video, Users, MoreVertical, Edit, Trash2,
  CheckCircle, AlertCircle, X, Loader2
} from 'lucide-react';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const CommunicationLog = () => {
  const [selectedSubject, setSelectedSubject] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [typeFilter, setTypeFilter] = useState('all');
  const [showNewEntry, setShowNewEntry] = useState(false);
  const [loading, setLoading] = useState(true);
  const [subjects, setSubjects] = useState([]);
  const [communications, setCommunications] = useState([]);
  const [newEntry, setNewEntry] = useState({
    type: 'email',
    direction: 'outbound',
    subject: '',
    notes: '',
    duration: '',
  });

  // Fetch subjects from backend
  const fetchSubjects = useCallback(async () => {
    try {
      const response = await fetch(`${API_URL}/api/registry/subjects`, {
        credentials: 'include'
      });
      if (response.ok) {
        const data = await response.json();
        // Transform subjects for display
        const transformedSubjects = data.subjects.map(s => ({
          id: s.rid,
          name: `Subject ${s.ssid.split('-').pop()}`,
          study: s.condition,
          ssid: s.ssid
        }));
        setSubjects(transformedSubjects);
      }
    } catch (err) {
      console.error('Failed to fetch subjects:', err);
    }
  }, []);

  // Fetch communications from backend
  const fetchCommunications = useCallback(async () => {
    try {
      const url = selectedSubject 
        ? `${API_URL}/api/registry/communications?subject_id=${selectedSubject.id}`
        : `${API_URL}/api/registry/communications`;
      
      const response = await fetch(url, {
        credentials: 'include'
      });
      if (response.ok) {
        const data = await response.json();
        setCommunications(data.communications.map(c => ({
          id: c.id,
          subjectId: c.subject_id,
          type: c.type,
          direction: c.direction,
          subject: c.subject,
          notes: c.notes,
          date: c.date,
          time: c.time,
          user: c.user,
          duration: c.duration,
          status: c.status
        })));
      }
    } catch (err) {
      console.error('Failed to fetch communications:', err);
    } finally {
      setLoading(false);
    }
  }, [selectedSubject]);

  useEffect(() => {
    fetchSubjects();
  }, [fetchSubjects]);

  useEffect(() => {
    fetchCommunications();
  }, [fetchCommunications]);

  const handleAddEntry = async () => {
    if (!selectedSubject) {
      toast.error('Please select a subject first');
      return;
    }
    if (!newEntry.subject || !newEntry.notes) {
      toast.error('Please fill in all required fields');
      return;
    }

    try {
      const response = await fetch(`${API_URL}/api/registry/communications`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({
          subject_id: selectedSubject.id,
          type: newEntry.type,
          direction: newEntry.direction,
          subject: newEntry.subject,
          notes: newEntry.notes,
          duration: newEntry.duration || null
        })
      });

      if (response.ok) {
        toast.success('Communication logged successfully');
        setShowNewEntry(false);
        setNewEntry({ type: 'email', direction: 'outbound', subject: '', notes: '', duration: '' });
        fetchCommunications();
      } else {
        toast.error('Failed to log communication');
      }
    } catch (err) {
      toast.error('Failed to log communication');
    }
  };

  const handleDeleteEntry = async (commId) => {
    try {
      const response = await fetch(`${API_URL}/api/registry/communications/${commId}`, {
        method: 'DELETE',
        credentials: 'include'
      });

      if (response.ok) {
        toast.success('Communication deleted');
        fetchCommunications();
      } else {
        toast.error('Failed to delete communication');
      }
    } catch (err) {
      toast.error('Failed to delete communication');
    }
  };

  const getTypeIcon = (type, direction) => {
    switch (type) {
      case 'email':
        return <Mail className={`w-4 h-4 ${direction === 'inbound' ? 'text-green-600' : 'text-blue-600'}`} />;
      case 'phone':
        if (direction === 'inbound') return <PhoneIncoming className="w-4 h-4 text-green-600" />;
        if (direction === 'outbound') return <PhoneOutgoing className="w-4 h-4 text-blue-600" />;
        return <Phone className="w-4 h-4 text-gray-600" />;
      case 'sms':
        return <MessageSquare className={`w-4 h-4 ${direction === 'inbound' ? 'text-green-600' : 'text-blue-600'}`} />;
      case 'visit':
        return <Users className="w-4 h-4 text-purple-600" />;
      case 'video':
        return <Video className="w-4 h-4 text-orange-600" />;
      default:
        return <FileText className="w-4 h-4 text-gray-600" />;
    }
  };

  const getTypeBadge = (type) => {
    const styles = {
      email: 'bg-blue-100 text-blue-700',
      phone: 'bg-green-100 text-green-700',
      sms: 'bg-purple-100 text-purple-700',
      visit: 'bg-orange-100 text-orange-700',
      video: 'bg-pink-100 text-pink-700',
    };
    return (
      <span className={`px-2 py-0.5 rounded text-xs capitalize ${styles[type] || 'bg-gray-100 text-gray-700'}`}>
        {type}
      </span>
    );
  };

  const getDirectionBadge = (direction) => {
    return direction === 'inbound' 
      ? <span className="px-2 py-0.5 bg-green-50 text-green-600 rounded text-xs">Inbound</span>
      : <span className="px-2 py-0.5 bg-blue-50 text-blue-600 rounded text-xs">Outbound</span>;
  };

  const filteredCommunications = communications
    .filter(c => !selectedSubject || c.subjectId === selectedSubject.id)
    .filter(c => typeFilter === 'all' || c.type === typeFilter)
    .filter(c => 
      c.subject.toLowerCase().includes(searchTerm.toLowerCase()) ||
      c.notes.toLowerCase().includes(searchTerm.toLowerCase())
    )
    .sort((a, b) => new Date(b.date + ' ' + b.time) - new Date(a.date + ' ' + a.time));

  // Stats
  const stats = {
    total: filteredCommunications.length,
    emails: filteredCommunications.filter(c => c.type === 'email').length,
    calls: filteredCommunications.filter(c => c.type === 'phone').length,
    visits: filteredCommunications.filter(c => c.type === 'visit').length,
  };

  if (loading && subjects.length === 0) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="w-8 h-8 animate-spin text-[#1877F2]" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Stats Cards */}
      <div className="grid grid-cols-4 gap-4">
        <Card className="border-0 shadow-sm bg-gray-50">
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-gray-200 rounded-lg flex items-center justify-center">
                <FileText className="w-5 h-5 text-gray-600" />
              </div>
              <div>
                <p className="text-2xl font-bold text-gray-700">{stats.total}</p>
                <p className="text-xs text-gray-500">Total Entries</p>
              </div>
            </div>
          </CardContent>
        </Card>
        
        <Card className="border-0 shadow-sm bg-blue-50">
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-blue-100 rounded-lg flex items-center justify-center">
                <Mail className="w-5 h-5 text-blue-600" />
              </div>
              <div>
                <p className="text-2xl font-bold text-blue-700">{stats.emails}</p>
                <p className="text-xs text-blue-600">Emails</p>
              </div>
            </div>
          </CardContent>
        </Card>
        
        <Card className="border-0 shadow-sm bg-green-50">
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-green-100 rounded-lg flex items-center justify-center">
                <Phone className="w-5 h-5 text-green-600" />
              </div>
              <div>
                <p className="text-2xl font-bold text-green-700">{stats.calls}</p>
                <p className="text-xs text-green-600">Phone Calls</p>
              </div>
            </div>
          </CardContent>
        </Card>
        
        <Card className="border-0 shadow-sm bg-orange-50">
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-orange-100 rounded-lg flex items-center justify-center">
                <Users className="w-5 h-5 text-orange-600" />
              </div>
              <div>
                <p className="text-2xl font-bold text-orange-700">{stats.visits}</p>
                <p className="text-xs text-orange-600">In-Person</p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      <div className="grid md:grid-cols-3 gap-6">
        {/* Subject Selection */}
        <Card className="border-0 shadow-sm">
          <CardHeader className="pb-2">
            <CardTitle className="text-base font-medium text-gray-700 flex items-center">
              <User className="w-5 h-5 mr-2 text-[#1877F2]" />
              Select Subject
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-2">
              <div 
                className={`p-3 rounded-lg border cursor-pointer transition-all ${
                  !selectedSubject ? 'border-[#1877F2] bg-blue-50' : 'border-gray-200 hover:border-gray-300'
                }`}
                onClick={() => setSelectedSubject(null)}
              >
                <p className="font-medium text-gray-900">All Subjects</p>
                <p className="text-xs text-gray-500">View all communications</p>
              </div>
              {subjects.map((subject) => (
                <div 
                  key={subject.id}
                  className={`p-3 rounded-lg border cursor-pointer transition-all ${
                    selectedSubject?.id === subject.id 
                      ? 'border-[#1877F2] bg-blue-50' 
                      : 'border-gray-200 hover:border-gray-300'
                  }`}
                  onClick={() => setSelectedSubject(subject)}
                >
                  <div className="flex items-center gap-2">
                    <div className="w-8 h-8 bg-gray-200 rounded-full flex items-center justify-center font-bold text-gray-600 text-sm">
                      {subject.name.charAt(0)}
                    </div>
                    <div>
                      <p className="font-medium text-gray-900">{subject.name}</p>
                      <p className="text-xs text-gray-500">{subject.ssid}</p>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>

        {/* Communication Log */}
        <div className="md:col-span-2 space-y-4">
          {/* Filters and Add Button */}
          <Card className="border-0 shadow-sm">
            <CardContent className="p-4">
              <div className="flex flex-col md:flex-row gap-4">
                <div className="relative flex-1">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                  <Input
                    placeholder="Search communications..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    className="pl-10"
                  />
                </div>
                <select
                  value={typeFilter}
                  onChange={(e) => setTypeFilter(e.target.value)}
                  className="px-4 py-2 border rounded-lg bg-white"
                >
                  <option value="all">All Types</option>
                  <option value="email">Email</option>
                  <option value="phone">Phone</option>
                  <option value="sms">SMS</option>
                  <option value="visit">In-Person</option>
                  <option value="video">Video Call</option>
                </select>
                <Button 
                  className="bg-[#1877F2] hover:bg-[#166FE5]"
                  onClick={() => setShowNewEntry(true)}
                >
                  <Plus className="w-4 h-4 mr-1" />
                  Log Entry
                </Button>
              </div>
            </CardContent>
          </Card>

          {/* New Entry Form */}
          {showNewEntry && (
            <Card className="border-0 shadow-sm border-l-4 border-l-[#1877F2]">
              <CardHeader className="pb-2">
                <div className="flex items-center justify-between">
                  <CardTitle className="text-base font-medium text-gray-700">
                    New Communication Entry
                  </CardTitle>
                  <button onClick={() => setShowNewEntry(false)} className="p-1 hover:bg-gray-100 rounded">
                    <X className="w-4 h-4 text-gray-400" />
                  </button>
                </div>
              </CardHeader>
              <CardContent className="space-y-4">
                {!selectedSubject && (
                  <div className="p-3 bg-amber-50 border border-amber-200 rounded-lg text-sm text-amber-700">
                    <AlertCircle className="w-4 h-4 inline mr-1" />
                    Please select a subject from the list first
                  </div>
                )}
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="text-sm font-medium text-gray-700 mb-1 block">Type</label>
                    <select
                      value={newEntry.type}
                      onChange={(e) => setNewEntry({ ...newEntry, type: e.target.value })}
                      className="w-full px-3 py-2 border rounded-lg"
                    >
                      <option value="email">Email</option>
                      <option value="phone">Phone Call</option>
                      <option value="sms">SMS/Text</option>
                      <option value="visit">In-Person Visit</option>
                      <option value="video">Video Call</option>
                    </select>
                  </div>
                  <div>
                    <label className="text-sm font-medium text-gray-700 mb-1 block">Direction</label>
                    <select
                      value={newEntry.direction}
                      onChange={(e) => setNewEntry({ ...newEntry, direction: e.target.value })}
                      className="w-full px-3 py-2 border rounded-lg"
                    >
                      <option value="outbound">Outbound (We contacted them)</option>
                      <option value="inbound">Inbound (They contacted us)</option>
                    </select>
                  </div>
                </div>
                <div>
                  <label className="text-sm font-medium text-gray-700 mb-1 block">Subject/Purpose</label>
                  <Input
                    value={newEntry.subject}
                    onChange={(e) => setNewEntry({ ...newEntry, subject: e.target.value })}
                    placeholder="e.g., Scheduling follow-up visit"
                  />
                </div>
                <div>
                  <label className="text-sm font-medium text-gray-700 mb-1 block">Notes</label>
                  <Textarea
                    value={newEntry.notes}
                    onChange={(e) => setNewEntry({ ...newEntry, notes: e.target.value })}
                    placeholder="Enter details about the communication..."
                    rows={3}
                  />
                </div>
                {(newEntry.type === 'phone' || newEntry.type === 'video' || newEntry.type === 'visit') && (
                  <div>
                    <label className="text-sm font-medium text-gray-700 mb-1 block">Duration</label>
                    <Input
                      value={newEntry.duration}
                      onChange={(e) => setNewEntry({ ...newEntry, duration: e.target.value })}
                      placeholder="e.g., 15 min"
                    />
                  </div>
                )}
                <div className="flex justify-end gap-2">
                  <Button variant="outline" onClick={() => setShowNewEntry(false)}>Cancel</Button>
                  <Button 
                    className="bg-[#1877F2] hover:bg-[#166FE5]"
                    onClick={handleAddEntry}
                    disabled={!selectedSubject}
                  >
                    <CheckCircle className="w-4 h-4 mr-1" />
                    Save Entry
                  </Button>
                </div>
              </CardContent>
            </Card>
          )}

          {/* Communication List */}
          <Card className="border-0 shadow-sm">
            <CardHeader className="pb-2">
              <CardTitle className="text-base font-medium text-gray-700 flex items-center justify-between">
                <span>Communication History</span>
                <span className="text-sm font-normal text-gray-500">
                  {filteredCommunications.length} entries
                </span>
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-3">
                {filteredCommunications.length === 0 ? (
                  <div className="text-center py-8 text-gray-400">
                    <MessageSquare className="w-12 h-12 mx-auto mb-3 opacity-50" />
                    <p>No communications found</p>
                  </div>
                ) : (
                  filteredCommunications.map((comm) => {
                    const subject = subjects.find(s => s.id === comm.subjectId);
                    return (
                      <div 
                        key={comm.id}
                        className="p-4 rounded-lg border border-gray-200 hover:border-gray-300 transition-all"
                      >
                        <div className="flex items-start justify-between mb-2">
                          <div className="flex items-center gap-3">
                            <div className="w-10 h-10 bg-gray-100 rounded-full flex items-center justify-center">
                              {getTypeIcon(comm.type, comm.direction)}
                            </div>
                            <div>
                              <p className="font-medium text-gray-900">{comm.subject}</p>
                              <div className="flex items-center gap-2 mt-1">
                                {getTypeBadge(comm.type)}
                                {getDirectionBadge(comm.direction)}
                                {!selectedSubject && (
                                  <span className="text-xs text-gray-500">• {subject?.name}</span>
                                )}
                              </div>
                            </div>
                          </div>
                          <div className="text-right text-xs text-gray-500">
                            <p>{comm.date}</p>
                            <p>{comm.time}</p>
                          </div>
                        </div>
                        <p className="text-sm text-gray-600 mb-2">{comm.notes}</p>
                        <div className="flex items-center justify-between text-xs text-gray-400">
                          <span>By: {comm.user}</span>
                          <div className="flex items-center gap-2">
                            {comm.duration && <span>Duration: {comm.duration}</span>}
                            <span className="flex items-center gap-1">
                              <CheckCircle className="w-3 h-3 text-green-500" />
                              {comm.status}
                            </span>
                          </div>
                        </div>
                      </div>
                    );
                  })
                )}
              </div>
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
};

export default CommunicationLog;
